import { Theme, ThemeFormData, BusinessThemeSummaryDto } from '../types';
import { apiClient } from './apiClient';

// Theme Service
export class ThemeService {
  // Create a new theme
  static async createTheme(themeData: ThemeFormData, vendorPhone?: string): Promise<Theme> {
    const config: any = {};
    if (vendorPhone) {
      config.headers = {
        'X-Vendor-Phone': vendorPhone
      };
    }
    try {
      const response = await apiClient.post('/themes', themeData, config);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('You are not authorized to create products. Only vendors can create products.');
      }
      throw error;
    }
  }

  // Get all themes
  static async getAllThemes(): Promise<Theme[]> {
    const response = await apiClient.get('/themes');
    return response.data;
  }

  // Get theme by ID
  static async getThemeById(themeId: string): Promise<Theme> {
    const response = await apiClient.get(`/themes/${themeId}`);
    return response.data;
  }

  // Get themes by business ID
  static async getThemesByBusinessId(businessId: string): Promise<Theme[]> {
    const response = await apiClient.get(`/themes/business/${businessId}`);
    return response.data;
  }

  // Get themes by category
  static async getThemesByCategory(category: string): Promise<Theme[]> {
    const response = await apiClient.get(`/themes/category/${category}`);
    return response.data;
  }

  // Get themes by active status
  static async getThemesByActive(active: boolean): Promise<Theme[]> {
    const response = await apiClient.get(`/themes/active/${active}`);
    return response.data;
  }

  // Update theme
  static async updateTheme(themeId: string, themeData: Partial<ThemeFormData>, vendorPhone?: string): Promise<Theme> {
    const config: any = {};
    if (vendorPhone) {
      config.headers = {
        'X-Vendor-Phone': vendorPhone
      };
    }
    try {
      const response = await apiClient.put(`/themes/${themeId}`, themeData, config);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('You are not authorized to update this product. You can only update your own products.');
      }
      throw error;
    }
  }

  // Delete theme
  static async deleteTheme(themeId: string, vendorPhone?: string): Promise<void> {
    const config: any = {};
    if (vendorPhone) {
      config.headers = {
        'X-Vendor-Phone': vendorPhone
      };
    }
    try {
      await apiClient.delete(`/themes/${themeId}`, config);
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('You are not authorized to delete this product. Only vendors can delete their own products.');
      }
      throw error;
    }
  }

  // Get theme count
  static async getThemeCount(): Promise<number> {
    const response = await apiClient.get('/themes/count');
    return response.data;
  }

  // Get minimal grouped view: business name + themes
  static async getThemesByBusinessSummary(): Promise<BusinessThemeSummaryDto[]> {
    const response = await apiClient.get('/themes/by-business/summary');
    return response.data;
  }
}

export default ThemeService;
